// src/components/sales/client-interactions/ClientInteractionsView.jsx
import React, { useMemo } from "react";
import { Plus, RefreshCw, Pencil, Trash2, Filter, X, MessageSquare } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export default function ClientInteractionsView({
  loading,
  clients,
  interactions,
  clientMap,
  clientIdFilter,
  setClientIdFilter,
  onApplyFilter,
  onClearFilter,
  onRefresh,
  onCreateClick,
  onEdit,
  onDelete,
}) {
  const headers = useMemo(
    () => ["Client", "Type", "Client Request", "Report", "Date", "Actions"],
    []
  );

  return (
    <div className="text-white">
      {/* Header (بديل الـ nav القديم) */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-white/10 border border-white/20 backdrop-blur-xl">
            <MessageSquare className="w-6 h-6 text-pink-200" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Client Interactions</h2>
            <p className="text-xs text-white/70">Track requests & reports per client</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={onCreateClick}
            className="px-4 py-2 rounded-xl bg-purple-600/80 hover:bg-purple-700 text-white flex items-center gap-2 backdrop-blur-lg"
          >
            <Plus className="w-5 h-5" />
            Add Interaction
          </button>

          <button
            onClick={onRefresh}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filter Card */}
      <div className="mb-6 backdrop-blur-xl bg-white/10 rounded-2xl border border-white/20 shadow-[0_0_40px_rgba(255,0,255,0.15)] p-5">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <label className="text-sm text-gray-200">Filter by Client</label>
            <select
              value={clientIdFilter}
              onChange={(e) => setClientIdFilter(e.target.value)}
              className="mt-2 w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-xl outline-none focus:ring-2 focus:ring-pink-400/30"
            >
              <option value="" className="text-black">
                All Clients
              </option>
              {(clients || []).map((c) => (
                <option key={c.id} value={String(c.id)} className="text-black">
                  {c.companyName || "Client"} — #{c.id}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onApplyFilter}
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            >
              <Filter className="w-5 h-5" />
              Apply
            </button>

            <button
              onClick={onClearFilter}
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            >
              <X className="w-5 h-5" />
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="backdrop-blur-xl bg-white/10 rounded-2xl shadow-[0_0_40px_rgba(255,0,255,0.20)] border border-white/20 overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-pink-500/20 to-purple-600/30 backdrop-blur-xl flex items-center justify-between">
          <h3 className="text-lg font-bold">Interactions</h3>
          <span className="text-sm text-gray-200">Total: {interactions?.length || 0}</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-white">
            <thead className="bg-white/10">
              <tr>
                {headers.map((h) => (
                  <th key={h} className="px-4 py-3 text-right text-sm font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-white/10">
              {(interactions || []).map((x) => {
                const client = clientMap?.get(String(x.clientId));
                return (
                  <tr key={x.id} className="hover:bg-white/10 transition">
                    <td className="px-4 py-3 text-right">
                      <div className="font-semibold">
                        {client?.companyName || `Client #${x.clientId ?? "-"}`}
                      </div>
                      <div className="text-xs text-gray-300">
                        {client?.emailAddress || client?.phoneNumber || ""}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <span className="px-2 py-1 rounded text-xs bg-white/10 border border-white/20">
                        {x.interactionType || "-"}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="text-sm whitespace-pre-wrap break-words max-w-[420px]">
                        {x.clientRequest || "-"}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="text-sm whitespace-pre-wrap break-words max-w-[420px]">
                        {x.report || "-"}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="text-sm">{formatDate(x.interactionDate)}</div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => onEdit?.(x)}
                          className="px-3 py-1.5 rounded-lg bg-pink-600/80 hover:bg-pink-700 text-white text-xs backdrop-blur-lg flex items-center gap-1"
                        >
                          <Pencil className="w-4 h-4" />
                          Edit
                        </button>

                        <button
                          onClick={() => onDelete?.(x.id)}
                          className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 text-red-100 text-xs backdrop-blur-lg flex items-center gap-1"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && (!interactions || interactions.length === 0) && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={6}>
                    No interactions yet.
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={6}>
                    Loading...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
